/**
 * AI 以图生图领域服务：OSS 上传 + /api/art/ai-image-edit/edit
 * UI / CDP 编辑器不直接发 HTTP。
 */

import { aiImageEditService } from '@/services/artApi';
import {
  ATTACHMENT_BUSINESS_TYPE,
  ATTACHMENT_FOLDER,
  uploadFileToOss,
} from '@/services/attachmentStore';
import { unwrapApiResult } from '@/utils/artMapper';
import { dataUrlToFile } from '@/utils/platformHelpers';

const AI_RENDER_PNG_TYPE = 'image/png';
let aiEditInFlight = null;

function resolveBusinessId(businessId) {
  const numeric = Number(String(businessId ?? '').replace(/^s/i, ''));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
}

function isUsableDataUrl(value) {
  const raw = String(value ?? '').trim();
  return raw.startsWith('data:image/') && raw.includes(',');
}

function isUsableHttpUrl(value) {
  return /^https?:\/\//i.test(String(value ?? '').trim());
}

export function parseAiImageEditResult(payload) {
  if (payload == null || typeof payload !== 'object') {
    throw new Error('AI 渲染接口无返回数据');
  }
  if (payload.success !== true || payload.error === true) {
    throw new Error(payload.errorDesc || payload.msg || payload.message || 'AI 渲染失败，请稍后重试');
  }
  const result = unwrapApiResult(payload);
  const fileUrls = Array.isArray(result?.fileUrls)
    ? result.fileUrls
    : (Array.isArray(result) ? result : []);
  const imageUrl = String(fileUrls.find(item => String(item ?? '').trim()) ?? '').trim();
  if (!imageUrl) {
    throw new Error('AI 渲染未返回图片，请稍后重试');
  }
  return {
    imageUrl,
    fileUrls: fileUrls.map(item => String(item ?? '').trim()).filter(Boolean),
  };
}

function dataUrlMime(dataUrl) {
  const match = String(dataUrl ?? '').match(/^data:([^;,]+)/i);
  return String(match?.[1] || '').trim() || AI_RENDER_PNG_TYPE;
}

async function uploadPngDataUrl(dataUrl, filename, businessId) {
  if (!isUsableDataUrl(dataUrl)) {
    throw new Error('页面导出失败，无法上传图片');
  }
  const mime = dataUrlMime(dataUrl);
  const ext = /jpe?g/i.test(mime) ? 'jpg' : 'png';
  const safeName = String(filename || `page-export.${ext}`).replace(/\.(png|jpe?g)$/i, `.${ext}`);
  const file = dataUrlToFile(dataUrl, safeName, mime);
  if (!file) {
    throw new Error('页面图片转换失败');
  }
  const uploaded = await uploadFileToOss(file, {
    filename: safeName,
    businessId: resolveBusinessId(businessId),
    folder: ATTACHMENT_FOLDER.TEMPLATE,
    businessType: ATTACHMENT_BUSINESS_TYPE.ORDER,
  });
  const url = String(uploaded?.url ?? '').trim();
  if (!isUsableHttpUrl(url)) {
    throw new Error('OSS 上传失败，未返回图片地址');
  }
  return url;
}

/**
 * 将编辑器导出的两页 PNG 上传 OSS，再请求 AI 渲染。
 * from = 第二页，to = 第一页。
 */
export async function runAiImageEditFromPageExports({
  fromDataUrl,
  toDataUrl,
  businessId,
} = {}) {
  if (aiEditInFlight) return aiEditInFlight;

  aiEditInFlight = (async () => {
    if (!isUsableDataUrl(fromDataUrl)) {
      throw new Error('第二页导出失败，无法开始渲染');
    }
    if (!isUsableDataUrl(toDataUrl)) {
      throw new Error('第一页导出失败，无法开始渲染');
    }

    const stamp = Date.now();
    const [from, to] = await Promise.all([
      uploadPngDataUrl(fromDataUrl, `ai-render-from-${stamp}.jpg`, businessId),
      uploadPngDataUrl(toDataUrl, `ai-render-to-${stamp}.jpg`, businessId),
    ]);

    const payload = await aiImageEditService.edit(
      { from, to },
      { silentError: true },
    );
    return parseAiImageEditResult(payload);
  })().finally(() => {
    aiEditInFlight = null;
  });

  return aiEditInFlight;
}
